import { Helmet } from 'react-helmet-async';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Search, Plus, MessageCircle, Clock } from 'lucide-react';
import { useState } from 'react';
import { getTickets, getCompanies, getUsers } from '../../services/storage';
import type { Ticket } from '../../types';

export const Tickets = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [tickets] = useState<Ticket[]>(getTickets());
  const companies = getCompanies();
  const users = getUsers();

  const filteredTickets = tickets.filter(ticket => {
    const company = companies.find(c => c.id === ticket.companyId);
    const user = users.find(u => u.id === ticket.userId);
    return (
      ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user?.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const getStatusBadge = (status: Ticket['status']) => {
    const variants = {
      open: 'primary',
      in_progress: 'secondary',
      resolved: 'success',
      closed: 'default'
    } as const;
    
    const labels = {
      open: 'Aberto',
      in_progress: 'Em Andamento',
      resolved: 'Resolvido',
      closed: 'Fechado'
    };

    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  const getPriorityColor = (priority: Ticket['priority']) => {
    const colors = {
      low: 'text-neutral-600',
      medium: 'text-yellow-600',
      high: 'text-orange-600',
      urgent: 'text-red-600'
    };
    return colors[priority];
  };

  return (
    <DashboardLayout>
      <Helmet>
        <title>Tickets | Portal Ethics</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Tickets de Suporte</h1>
            <p className="text-neutral-600">Gerencie todos os tickets de suporte</p>
          </div>
          <Button variant="primary" className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Novo Ticket
          </Button>
        </div>

        {/* Search */}
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <Input
              placeholder="Buscar tickets..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Tickets List */}
        <div className="space-y-4">
          {filteredTickets.map((ticket) => {
            const company = companies.find(c => c.id === ticket.companyId);
            return (
              <Card key={ticket.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-neutral-900">{ticket.title}</h3>
                      {getStatusBadge(ticket.status)}
                    </div>
                    <p className="text-neutral-600 mb-4">{ticket.description}</p>
                    <div className="flex items-center gap-6 text-sm text-neutral-500">
                      <span className="flex items-center gap-2">
                        <MessageCircle className="w-4 h-4" />
                        {company?.name}
                      </span>
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {new Date(ticket.createdAt).toLocaleDateString('pt-BR')}
                      </span>
                      <span className={getPriorityColor(ticket.priority)}>
                        Prioridade: {ticket.priority === 'low' ? 'Baixa' : 
                                   ticket.priority === 'medium' ? 'Média' : 
                                   ticket.priority === 'high' ? 'Alta' : 'Urgente'}
                      </span>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    Ver Detalhes
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};
